import { Component, h } from "preact";
import {
  IImageMeta,
  ILabelOptionValue,
  IOption,
  IReactConfiguration,
  IDictionary,
  IAttribute,
} from "../../typings";
import OutputController from "../../shared/controller/output-controller";
import TemplateController from "../../shared/controller/template-controller";
import EventController from "../../shared/controller/event-controller";
import Uploader from "../uploader";
import LabelSizingComponent from "../label-options/types/LabelSizingComponent";
import LabelApplicationMethodComponent from "../label-options/types/LabelApplicationMethodComponent";
import LabelPhotoproofComponent from "../label-options/types/LabelPhotoproofComponent";
import LabelCordComponent from "../label-options/types/LabelCordComponent";
import LabelFileBackComponent from "../label-options/types/LabelFileBackComponent";
import LabelCommentsComponent from "../label-options/types/LabelCommentsComponent";
import LabelShapeComponent from "../label-options/types/LabelShapeComponent";
import UnitSystemController from "../../shared/controller/unit-system-controller";
import SizeController from "../../shared/controller/size-controller";
import LabelDoubleWhiteComponent from "../label-options/types/LabelDoubleWhiteComponent";
import LabelColorComponent from "../label-options/types/LabelColorComponent";
import ColorController from "../../shared/controller/color-controller";
import LabelColorTextComponent from "../label-options/types/LabelColorTextComponent";
import LabelSvgSprite from "./label-svg-sprite";
import RadioComponent from "../label-options/types/RadioComponent";

export default class LabelOption extends Component<
  ILabelOptionProps,
  ILabelOptionState
> {
  constructor(props: ILabelOptionProps = null) {
    super(props);

    this.state = {
      hide: false,
      selected: [],
      titleTranslation: "",
      descriptionTranslation: "",
      option: props.option,
    };

    this.resetToDefaultSize = this.resetToDefaultSize.bind(this);
    this.updateHideState = this.updateHideState.bind(this);
  }

  componentDidMount(): void {
    const state: ILabelOptionState = Object.assign(
      {
        option: this.props.option,
      },
      this.state,
    );

    if (this.props.option.meta.name) {
      state.titleTranslation = this.props.option.meta.name;
    }

    if (this.props.option.meta.description) {
      state.descriptionTranslation = this.props.option.meta.description;
    }

    this.setState(state);

    this.updateHideState();
    this.loadEventListeners();
  }

  private loadEventListeners(): void {
    this.loadEventListenersConditions(
      this.props.option.attributes?.hide?.value,
      this.updateHideState,
    );

    this.props.option.values.forEach((value: ILabelOptionValue) => {
      this.loadEventListenersConditions(value.attributes?.hide?.value, () => {
        this.setState({
          option: this.getOptionFilteredValues(),
        });
      });
    });
  }

  private loadEventListenersConditions(
    conditions: { option: string }[],
    callable: () => void,
  ): void {
    if (!Array.isArray(conditions)) {
      return;
    }

    conditions.forEach(
      (filter: { option: string; value: { option: string }[] }) => {
        const eventName: string = EventController.getSaveEventName([
          "label",
          filter.option,
        ]);
        document.body
          .querySelector(this.props.conf.target)
          .addEventListener(eventName, callable);
        this.loadEventListenersConditions(filter.value, callable);
      },
    );
  }

  updateHideState(): void {
    const hide: boolean = this.props.outputController.getOptionAttributeBoolean(
      this.props.option.code,
      "hide",
    );
    const value: any = this.props.outputController.getValue([
      "",
      this.props.option.code,
    ]);

    if (
      hide &&
      ((!Array.isArray(value) && value !== null) ||
        (Array.isArray(value) && value.length !== 0 && value[0] !== null))
    ) {
      this.props.outputController.setValue(["", this.props.option.code], null);
    }
    if (
      !hide &&
      this.props.option.default &&
      ((!Array.isArray(value) && value === null) ||
        (Array.isArray(value) && (value.length === 0 || value[0] === null)))
    ) {
      this.props.outputController.setValue(
        ["", this.props.option.code],
        this.props.option.default,
      );
    }

    this.setState({
      hide,
    });
  }

  getOptionFilteredValues(): IOption {
    return this.props.outputController.getOptionFilteredValues(
      this.props.option,
    );
  }

  private resetToDefaultSize(): void {
    const meta: IImageMeta = this.props.outputController.getValue([
      "image",
      "original-meta",
    ]);
    const imageData: Cropper.Data =
      this.props.sizeController.generateCroppedData(meta);
    this.props.sizeController.newImageCroppedData(imageData, meta);
  }

  render(): JSX.Element {
    const props: ILabelOptionProps = this.props,
      conf: IReactConfiguration = props.conf,
      main: Uploader = props.main,
      option: IOption = props.option,
      attributes: IDictionary<IAttribute> = option.attributes ?? {},
      state: Readonly<ILabelOptionState> = this.state,
      template: string = attributes.template?.value
        ? attributes.template?.value
        : option.code;

    let selector: string = conf.canvas_target;

    if (conf.target) {
      selector = conf.target + " " + selector;
    }

    if (this.state.hide) {
      return null;
    }

    return (
      <div className="optionBlock">
        <div className="optionTitleWrapper">
          <h3>{state.titleTranslation && state.titleTranslation}</h3>

          <div className="flex space-x-8">
            {template === "color" && (
              <div
                className={
                  "settings" +
                  (this.props.option.attributes?.color_auto_detected?.value
                    ? ""
                    : " hidden")
                }
                title={state.descriptionTranslation && option.meta.fields.reset}
                onClick={() => {
                  props.colorController.resetColors().catch(() => {
                    main.setMessage(
                      props.templateController.getTemplate().meta.fields
                        .errorColorsCouldNotBeExtracted,
                    );
                  });
                }}
              >
                <LabelSvgSprite
                  viewBox="0 0 32 32"
                  cssClass={"w-6 h-6"}
                  conf={conf}
                  imageCode="reset"
                  alt={"Reset"}
                />
              </div>
            )}

            {template === "sizing" && (
              <div
                className="settings"
                title={state.descriptionTranslation && option.meta.fields.reset}
                onClick={this.resetToDefaultSize}
              >
                <LabelSvgSprite
                  viewBox="0 0 32 32"
                  cssClass={"w-6 h-6"}
                  conf={conf}
                  imageCode="reset"
                  alt={"Reset"}
                />
              </div>
            )}

            {!attributes.hideTooltip?.value && state.descriptionTranslation && (
              <div className="relative 10 group">
                <button className="flex items-center justify-center w-6 h-6 border border-black rounded-full cursor-pointer">
                  <span className="select-none">i</span>
                </button>
                <dialog className="absolute z-50 group-hover:block">
                  {state.descriptionTranslation}
                </dialog>
              </div>
            )}
          </div>
        </div>

        <div
          className={`bg-gray-lighter rounded-b-lg ${
            template === "color" ? "rounded-b-lg" : "p-6 lg:p-8"
          }`}
        >
          {template === "shape" && (
            <LabelShapeComponent
              main={main}
              conf={conf}
              option={this.state.option}
              eventController={props.eventController}
              outputController={props.outputController}
              templateController={props.templateController}
              unitSystemController={props.unitSystemController}
            />
          )}

          {template === "application" && (
            <LabelApplicationMethodComponent
              main={main}
              conf={conf}
              option={this.state.option}
              eventController={props.eventController}
              outputController={props.outputController}
              sizeController={props.sizeController}
              templateController={props.templateController}
              unitSystemController={props.unitSystemController}
            />
          )}

          {template === "sizing" && (
            <LabelSizingComponent
              main={main}
              conf={conf}
              option={this.state.option}
              eventController={props.eventController}
              outputController={props.outputController}
              sizeController={props.sizeController}
              templateController={props.templateController}
              unitSystemController={props.unitSystemController}
            />
          )}

          {template === "photoproof" && (
            <LabelPhotoproofComponent
              main={main}
              conf={conf}
              option={this.state.option}
              eventController={props.eventController}
              outputController={props.outputController}
              templateController={props.templateController}
              unitSystemController={props.unitSystemController}
            />
          )}

          {template === "file-back" && (
            <LabelFileBackComponent
              main={main}
              conf={conf}
              option={this.state.option}
              eventController={props.eventController}
              outputController={props.outputController}
              templateController={props.templateController}
              unitSystemController={props.unitSystemController}
              selector={selector}
              templateLoaded={props.templateLoaded}
              sizeController={props.sizeController}
              colorController={props.colorController}
            />
          )}

          {template === "cord" && (
            <LabelCordComponent
              main={main}
              conf={conf}
              option={this.state.option}
              eventController={props.eventController}
              outputController={props.outputController}
              templateController={props.templateController}
              unitSystemController={props.unitSystemController}
            />
          )}

          {template === "doublewhite" && (
            <LabelDoubleWhiteComponent
              main={main}
              conf={conf}
              option={this.state.option}
              eventController={props.eventController}
              outputController={props.outputController}
              templateController={props.templateController}
              unitSystemController={props.unitSystemController}
            />
          )}

          {template === "comments" && (
            <LabelCommentsComponent
              main={main}
              conf={conf}
              option={this.state.option}
              eventController={props.eventController}
              outputController={props.outputController}
              templateController={props.templateController}
              unitSystemController={props.unitSystemController}
            />
          )}

          {template === "color" && (
            <LabelColorComponent
              main={main}
              conf={conf}
              option={this.state.option}
              eventController={props.eventController}
              outputController={props.outputController}
              templateController={props.templateController}
            />
          )}

          {template === "color-grid" && (
            <LabelColorTextComponent
              main={main}
              conf={conf}
              option={this.state.option}
              eventController={props.eventController}
              outputController={props.outputController}
              templateController={props.templateController}
            />
          )}

          {template === "radio" && (
            <RadioComponent
              main={main}
              conf={conf}
              option={this.state.option}
              eventController={props.eventController}
              outputController={props.outputController}
              templateController={props.templateController}
              unitSystemController={props.unitSystemController}
            />
          )}
        </div>
      </div>
    );
  }
}

interface ILabelOptionProps {
  conf: IReactConfiguration;
  main: Uploader;
  option: IOption;

  eventController: EventController;
  outputController: OutputController;
  sizeController: SizeController;
  templateController: TemplateController;
  unitSystemController: UnitSystemController;
  colorController: ColorController;
  templateLoaded: boolean;
}

interface ILabelOptionState {
  selected: Array<string>;
  titleTranslation: string;
  descriptionTranslation: string;
  hide: boolean;
  option: IOption;
}
